import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, TrendingUp, RotateCcw, X, Eye } from "lucide-react";
import { EtsyProduct } from "@/hooks/useEtsyProducts";
import { useCurrency } from "@/contexts/CurrencyContext";
import ProductImageSlideshow from "./ProductImageSlideshow";

interface ProductCardProps {
  product: EtsyProduct;
  onProductSelect: (productId: string) => void;
}

const ProductCard = ({ product, onProductSelect }: ProductCardProps) => {
  const { formatPrice } = useCurrency();

  const getStockStatus = (quantity?: number) => {
    if (quantity === undefined) return { label: "Unknown", variant: "secondary" as const };
    if (quantity === 0) return { label: "Out of Stock", variant: "destructive" as const };
    if (quantity <= 5) return { label: "Low Stock", variant: "default" as const };
    return { label: "In Stock", variant: "secondary" as const };
  };

  const hasDiscount = (product: EtsyProduct) => {
    return product.originalPrice && product.price && product.originalPrice > product.price;
  };

  // Use all available images from the product
  const getAllImageUrls = (product: EtsyProduct): string[] => {
    // Use the imageUrls array if available, otherwise fallback to mainImageUrl
    if (product.imageUrls && product.imageUrls.length > 0) {
      return product.imageUrls;
    }
    
    // Fallback to main image if imageUrls is not available
    if (product.mainImageUrl) {
      return [product.mainImageUrl];
    }
    
    return [];
  };

  const stockStatus = getStockStatus(product.quantity);
  const productHasDiscount = hasDiscount(product);
  const totalViews = (product as any).views || 0;
  const imageUrls = getAllImageUrls(product);

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <div className="relative">
        <ProductImageSlideshow 
          images={imageUrls}
          productTitle={product.title}
        />
        
        <div className="absolute top-2 right-2 flex gap-2">
          <Badge variant={stockStatus.variant}>
            {stockStatus.label}
          </Badge>
        </div>
        
        <div className="absolute top-2 left-2 flex gap-2">
          {productHasDiscount && (
            <Badge variant="destructive" className="bg-red-500">
              Sale
            </Badge>
          )}
        </div>
      </div>
      
      <CardHeader className="pb-3">
        <CardTitle className="text-lg line-clamp-2" title={product.title}>
          {product.title}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Price</span>
            <div className="text-right">
              {productHasDiscount ? (
                <div className="flex flex-col items-end">
                  <span className="font-semibold text-lg text-red-600">
                    {formatPrice(product.price || 0, product.currencyCode)}
                  </span>
                  <span className="text-sm text-gray-500 line-through">
                    {formatPrice(product.originalPrice || 0, product.currencyCode)}
                  </span>
                </div>
              ) : (
                <span className="font-semibold text-lg">
                  {formatPrice(product.price || 0, product.currencyCode)}
                </span>
              )}
            </div>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Stock</span>
            <span className="font-medium">
              {product.quantity !== undefined ? product.quantity : 'Unknown'}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Total Views</span>
            <div className="flex items-center gap-1">
              <Eye className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-blue-600">
                {totalViews.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Sales</span>
            <div className="flex items-center gap-1">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <span className="font-medium text-green-600">
                {product.salesCount || 0}
              </span>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Refunds</span>
            <div className="flex items-center gap-1">
              <RotateCcw className="h-4 w-4 text-orange-600" />
              <span className="font-medium text-orange-600">
                {product.refundCount || 0}
              </span>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Cancelled</span>
            <div className="flex items-center gap-1">
              <X className="h-4 w-4 text-red-600" />
              <span className="font-medium text-red-600">
                {product.cancelledCount || 0}
              </span>
            </div>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Status</span>
            <Badge variant="outline" className="capitalize">
              {product.state || 'Unknown'}
            </Badge>
          </div>
          
          {product.url && (
            <div className="pt-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => window.open(product.url, '_blank')}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                View on Etsy
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
